import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import completionPopper from '@/assets/learner/completion-popper.svg'
import { useFocusTrap } from '@/hooks/useFocusTrap'
import { Button } from './ui/Button'

export interface CompletionCelebrationProps {
  title?: string
  /** An empty message hides the description. Omission uses the default grading guide. */
  message?: string
  ctaLabel?: string
  onCtaClick: () => void
}

export function CompletionCelebration({
  title,
  message,
  ctaLabel,
  onCtaClick,
}: CompletionCelebrationProps) {
  const { t } = useTranslation('learner')
  const dialogRef = useRef<HTMLDivElement | null>(null)
  useFocusTrap(dialogRef, true)

  const resolvedTitle = title ?? t('learn.celebration.title')
  const resolvedMessage = message ?? t('learn.celebration.message')

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="completion-dialog-title"
        aria-describedby={resolvedMessage ? 'completion-dialog-description' : undefined}
        className="flex w-full max-w-[360px] flex-col items-center gap-6 rounded-[24px] bg-lc-bg-primary px-6 pb-6 pt-8 text-center shadow-[0_8px_8px_rgb(0_0_0/0.1)]"
      >
        <div className="flex size-20 shrink-0 items-center justify-center rounded-full bg-lc-key-50">
          <img src={completionPopper} alt="" className="size-10" />
        </div>

        <div className="flex w-full flex-col gap-4">
          <h2
            id="completion-dialog-title"
            className="text-lc-v3-h4 font-bold text-lc-text-primary"
          >
            {resolvedTitle}
          </h2>
          {resolvedMessage ? <p
            id="completion-dialog-description"
            className="whitespace-pre-line text-lc-v3-body-1 font-normal text-lc-text-secondary"
          >
            {resolvedMessage}
          </p> : null}
        </div>

        <Button
          variant="primary"
          size="L"
          className="min-w-[120px]"
          onClick={onCtaClick}
        >
          {ctaLabel ?? t('learn.celebration.cta')}
        </Button>
      </div>
    </div>
  )
}
